import {
  Alert,
  Checkbox,
  CountryInput,
  DateField,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  cn,
  getDaysInMonth,
  NonTimezoneSensitiveDate,
} from '@commenda/utils';
import { Country } from '@prisma/client';
import { AddEntityFormController, AddEntityFormData } from './useAddEntityForm';

interface AddEntityFormFieldsProps {
  form: AddEntityFormController;
  className?: string;
  disabledFields?: Partial<Record<keyof AddEntityFormData, boolean>>;
  // Renders the "external entity" checkbox. Bottomsheet only.
  showNonControlledEntity?: boolean;
  hideDateOfIncorporation?: boolean;
  hideFiscalYearEnd?: boolean;
  hideEditCountryAlert?: boolean;
  requiredAsterisks?: boolean;
  countryLabel?: string;
  companyLegalNamePlaceholder?: string;
  // Inline style — Tailwind's content scan misses some arbitrary classes
  // from `packages/ui` in the consuming app.
  containerStyle?: React.CSSProperties;
}

export const AddEntityFormFields = ({
  form,
  className,
  disabledFields,
  showNonControlledEntity = false,
  hideDateOfIncorporation = false,
  hideFiscalYearEnd = false,
  hideEditCountryAlert = false,
  requiredAsterisks = true,
  countryLabel = 'Country',
  companyLegalNamePlaceholder = "Enter your company's legal name",
  containerStyle,
}: AddEntityFormFieldsProps) => {
  const {
    formData,
    errors,
    setFormData,
    setErrors,
    updateField,
    isRequired,
    businessEntityId,
    shouldShowJurisdictionInput,
    shouldShowEntityTypeInput,
    shouldShowFiscalYearInput,
    isFiscalYearLocked,
    jurisdictionOptions,
    entityTypeOptions,
    governanceOptions,
    requiresGovernanceElection,
    offersGovernanceElection,
    electedStructureCode,
  } = form;

  const defaultStructure = governanceOptions.find((structure) => structure.isDefault);

  const showRequired = isRequired && requiredAsterisks;
  const renderLabel = (htmlFor: string, text: string) => (
    <Label htmlFor={htmlFor} required={showRequired}>
      {text}
    </Label>
  );

  return (
    <div className={cn('flex flex-col gap-xl', className)} style={containerStyle}>
      {businessEntityId && !hideEditCountryAlert ? (
        <Alert
          variant="info"
          _className="border border-utility-blue-100"
          descriptionClassName="txt-small-medium text-text-primary"
          description="You can't change this entity's country. To update it, delete the entity and create a new one with the correct country."
        />
      ) : null}

      <FieldWrapper>
        {renderLabel('incorporationCountry', countryLabel)}
        <CountryInput
          disabled={!!businessEntityId || disabledFields?.incorporationCountry}
          value={formData.incorporationCountry}
          onValueChange={(val: Country) => {
            updateField('incorporationCountry', val);
          }}
          type="single"
          _className="important-txt-medium-regular h-11"
          error={errors?.incorporationCountry}
        />
      </FieldWrapper>

      {formData.incorporationCountry ? (
        <FieldWrapper>
          {renderLabel('companyLegalName', 'Company legal name')}
          <Input
            id="companyLegalName"
            data-testid="entity-legal-name"
            value={formData.companyLegalName}
            onChange={(e) => updateField('companyLegalName', e.target.value)}
            placeholder={companyLegalNamePlaceholder}
            error={errors?.companyLegalName}
          />
        </FieldWrapper>
      ) : null}

      {!hideDateOfIncorporation && formData.incorporationCountry ? (
        <FieldWrapper>
          {renderLabel('dateOfIncorporation', 'Date of incorporation')}
          <DateField
            value={formData.dateOfIncorporation}
            onChange={(val) => updateField('dateOfIncorporation', val ?? '')}
            disallowFutureDates
            fullWidth
            error={errors?.dateOfIncorporation}
          />
        </FieldWrapper>
      ) : null}

      {shouldShowJurisdictionInput ? (
        <FieldWrapper>
          {renderLabel('jurisdiction', 'Jurisdiction')}
          <Select
            value={formData.jurisdiction ?? undefined}
            onValueChange={(v) => {
              setFormData((prev) => ({
                ...prev,
                jurisdiction: v,
                entityLegalFormId: null,
                entityType: null,
              }));
              setErrors((prev) => {
                const next = { ...prev };
                delete next.jurisdiction;
                return next;
              });
            }}
          >
            <SelectTrigger error={errors?.jurisdiction}>
              <SelectValue placeholder="Select jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              {jurisdictionOptions.map((j) => (
                <SelectItem value={j.id} key={j.id}>
                  {j.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.jurisdiction ? (
            <InputErrorMessage message={errors.jurisdiction} />
          ) : null}
        </FieldWrapper>
      ) : null}

      {shouldShowEntityTypeInput ? (
        <FieldWrapper>
          {renderLabel('entityType', 'Entity type')}
          <Select
            key={formData.jurisdiction ?? 'no-jurisdiction'}
            value={formData.entityLegalFormId ?? undefined}
            onValueChange={(v) => {
              setFormData((prev) => ({
                ...prev,
                entityLegalFormId: v,
                entityType:
                  entityTypeOptions.find(
                    (option) => option.entityLegalFormId === v,
                  )?.corporationType ?? null,
              }));
              setErrors((prev) => {
                const next = { ...prev };
                delete next.entityType;
                return next;
              });
            }}
            disabled={!formData.jurisdiction}
          >
            <SelectTrigger error={errors?.entityType}>
              <SelectValue placeholder="Select entity type" />
            </SelectTrigger>
            <SelectContent>
              {entityTypeOptions.map((et) => (
                <SelectItem
                  key={et.entityLegalFormId}
                  value={et.entityLegalFormId}
                >
                  {et.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.entityType ? (
            <InputErrorMessage message={errors.entityType} />
          ) : null}
        </FieldWrapper>
      ) : null}

      {offersGovernanceElection ? (
        <FieldWrapper>
          <Label
            htmlFor="governanceStructureCode"
            required={showRequired && requiresGovernanceElection}
          >
            Governance
          </Label>
          <Select
            key={formData.entityLegalFormId ?? 'no-entity-type'}
            value={electedStructureCode ?? undefined}
            onValueChange={(v) => updateField('governanceStructureCode', v)}
          >
            <SelectTrigger error={errors?.governanceStructureCode}>
              <SelectValue
                placeholder={
                  defaultStructure
                    ? `${defaultStructure.structureLocalTerm || defaultStructure.bodyLocalTerm} (default)`
                    : 'Select how this entity is governed'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {governanceOptions.map((structure) => (
                <SelectItem
                  key={structure.structureCode}
                  value={structure.structureCode}
                >
                  {structure.structureLocalTerm || structure.bodyLocalTerm}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.governanceStructureCode ? (
            <InputErrorMessage message={errors.governanceStructureCode} />
          ) : null}
        </FieldWrapper>
      ) : null}

      {!hideFiscalYearEnd && shouldShowFiscalYearInput ? (
        <FieldWrapper>
          {renderLabel('fiscalYearEnd', 'Fiscal year end')}
          <div className="flex gap-2">
            <div className="flex-1 flex flex-col gap-1.5">
              <Select
                disabled={isFiscalYearLocked}
                value={formData.fiscalYearEnd?.month?.toString()}
                onValueChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    fiscalYearEnd: {
                      month: v,
                      day: prev.fiscalYearEnd?.day ?? '',
                    },
                  }));
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next['fiscalYearEnd.month'];
                    return next;
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month, index) => (
                    <SelectItem key={month} value={month.toString()}>
                      {NonTimezoneSensitiveDate.getMonthNames()[index]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors?.['fiscalYearEnd.month'] && (
                <InputErrorMessage message={errors['fiscalYearEnd.month']} />
              )}
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
              <Select
                disabled={isFiscalYearLocked}
                value={formData.fiscalYearEnd?.day?.toString()}
                onValueChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    fiscalYearEnd: {
                      month: prev.fiscalYearEnd?.month ?? '',
                      day: v,
                    },
                  }));
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next['fiscalYearEnd.day'];
                    return next;
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(
                    new Array(
                      formData.fiscalYearEnd?.month
                        ? getDaysInMonth(
                            parseInt(formData.fiscalYearEnd.month, 10),
                            2001,
                          )
                        : 31,
                    ),
                  ).map((_, idx) => (
                    <SelectItem
                      key={(idx + 1).toString()}
                      value={(idx + 1).toString()}
                    >
                      {idx + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors?.['fiscalYearEnd.day'] && (
                <InputErrorMessage message={errors['fiscalYearEnd.day']} />
              )}
            </div>
          </div>
          {isFiscalYearLocked ? (
            <p className="txt-small-regular text-text-tertiary">
              Set by law for this entity type and can&apos;t be changed.
            </p>
          ) : null}
        </FieldWrapper>
      ) : null}

      {showNonControlledEntity ? (
        <FieldWrapper>
          <label className="flex items-start gap-md cursor-pointer">
            <Checkbox
              checked={formData.isNonControlledEntity}
              onCheckedChange={(checked) =>
                updateField('isNonControlledEntity', checked === true)
              }
            />
            <span className="flex flex-col gap-xs">
              <span className="txt-small-bold text-text-primary">
                This is an external entity
              </span>
              <span className="txt-small-regular text-text-secondary">
                Use this for joint ventures, partner-owned entities, or any
                entity you want to model but don&apos;t control. It&apos;ll
                appear on your org chart with limited access to additional
                features.
              </span>
            </span>
          </label>
        </FieldWrapper>
      ) : null}
    </div>
  );
};
